import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

type ParticleVariant = 'logo' | 'ambient' | 'corner';
type ParticleDensity = 'low' | 'medium' | 'hero';

interface Particle {
  baseX: number;
  baseY: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  phase: number;
  alpha: number;
}

interface MirakelParticlesProps {
  variant?: ParticleVariant;
  density?: ParticleDensity;
  text?: string;
  opacity?: number;
  sx?: SxProps<Theme>;
  className?: string;
}

const colors = ['#2563EB', '#06B6D4', '#F97316'];

const densityCounts: Record<ParticleDensity, number> = {
  low: 34,
  medium: 70,
  hero: 1500,
};

const getAmbientCount = (
  density: ParticleDensity,
  width: number,
  height: number,
) => {
  const areaScale = Math.min(1.45, Math.max(0.65, (width * height) / 90000));
  return Math.round(densityCounts[density] * areaScale);
};

const createAmbientParticles = (
  width: number,
  height: number,
  density: ParticleDensity,
  variant: ParticleVariant,
  prefersReducedMotion: boolean,
) => {
  const count = getAmbientCount(density, width, height);

  return Array.from({ length: count }, (_, index) => {
    const isCorner = variant === 'corner';
    const baseX = isCorner
      ? width * (0.55 + Math.random() * 0.42)
      : Math.random() * width;
    const wave = Math.sin((index / Math.max(count - 1, 1)) * Math.PI);
    const baseY = isCorner
      ? height * (0.04 + Math.random() * 0.62)
      : height * (0.16 + Math.random() * 0.68 + wave * 0.08);

    return {
      baseX,
      baseY,
      x: prefersReducedMotion ? baseX : baseX + (Math.random() - 0.5) * 36,
      y: prefersReducedMotion ? baseY : baseY + (Math.random() - 0.5) * 28,
      vx: 0,
      vy: 0,
      color: colors[index % colors.length],
      size: 1 + Math.random() * 1.65,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.28 + Math.random() * 0.46,
    };
  });
};

const createLogoParticles = (
  width: number,
  height: number,
  density: ParticleDensity,
  text: string,
  prefersReducedMotion: boolean,
) => {
  const textCanvas = document.createElement('canvas');
  const textContext = textCanvas.getContext('2d');

  if (!textContext) {
    return [];
  }

  textCanvas.width = width;
  textCanvas.height = height;

  const fontSize = Math.min(width / 4.6, height / 4, 230);
  textContext.font = `800 ${fontSize}px Inter, Arial, sans-serif`;
  textContext.textAlign = 'center';
  textContext.textBaseline = 'middle';
  textContext.fillStyle = '#0F172A';
  textContext.fillText(text, width / 2, height * 0.43);

  const imageData = textContext.getImageData(0, 0, width, height).data;
  const coordinates: { x: number; y: number }[] = [];
  const step = width < 700 ? 7 : 6;

  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const alphaValue = imageData[(y * width + x) * 4 + 3];

      if (alphaValue > 128) {
        coordinates.push({ x, y });
      }
    }
  }

  const maxParticles =
    density === 'hero' ? (width < 700 ? 900 : 1500) : densityCounts[density];
  const stride = Math.max(1, Math.ceil(coordinates.length / maxParticles));

  return coordinates
    .filter((_, index) => index % stride === 0)
    .map((coordinate, index) => ({
      baseX: coordinate.x,
      baseY: coordinate.y,
      x: prefersReducedMotion ? coordinate.x : Math.random() * width,
      y: prefersReducedMotion ? coordinate.y : Math.random() * height,
      vx: 0,
      vy: 0,
      color: colors[index % colors.length],
      size: 1.15 + Math.random() * 1.35,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.72,
    }));
};

const MirakelParticles: React.FC<MirakelParticlesProps> = ({
  variant = 'ambient',
  density = 'low',
  text = 'MiRAKEL',
  opacity = 0.35,
  sx,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    let animationFrame = 0;
    let particles: Particle[] = [];
    let tick = 0;
    let canvasWidth = 0;
    let canvasHeight = 0;

    const createParticles = () => {
      const bounds =
        canvas.parentElement?.getBoundingClientRect() ||
        canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(bounds.width || window.innerWidth));
      const height = Math.max(
        1,
        Math.round(bounds.height || window.innerHeight),
      );
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      canvasWidth = width;
      canvasHeight = height;
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      particles =
        variant === 'logo'
          ? createLogoParticles(
              width,
              height,
              density,
              text,
              prefersReducedMotion,
            )
          : createAmbientParticles(
              width,
              height,
              density,
              variant,
              prefersReducedMotion,
            );
    };

    const draw = () => {
      tick += variant === 'logo' ? 0.012 : 0.009;
      context.clearRect(0, 0, canvasWidth, canvasHeight);

      if (variant !== 'logo') {
        context.lineWidth = 1;
        particles.forEach((particle, index) => {
          const nextParticle = particles[(index + 5) % particles.length];
          if (!nextParticle) return;

          const xDelta = nextParticle.x - particle.x;
          const yDelta = nextParticle.y - particle.y;
          const distance = Math.sqrt(xDelta * xDelta + yDelta * yDelta);

          if (distance < 86) {
            context.beginPath();
            context.globalAlpha = Math.max(0, 0.1 - distance / 900);
            context.strokeStyle = particle.color;
            context.moveTo(particle.x, particle.y);
            context.lineTo(nextParticle.x, nextParticle.y);
            context.stroke();
          }
        });
      }

      particles.forEach((particle) => {
        const driftX = prefersReducedMotion
          ? 0
          : Math.sin(tick + particle.phase) * (variant === 'logo' ? 2.8 : 4.8);
        const driftY = prefersReducedMotion
          ? 0
          : Math.cos(tick * 0.9 + particle.phase) *
            (variant === 'logo' ? 2 : 3.2);
        const targetX = particle.baseX + driftX;
        const targetY = particle.baseY + driftY;

        particle.vx +=
          (targetX - particle.x) * (variant === 'logo' ? 0.018 : 0.026);
        particle.vy +=
          (targetY - particle.y) * (variant === 'logo' ? 0.018 : 0.026);
        particle.vx *= 0.86;
        particle.vy *= 0.86;
        particle.x += particle.vx;
        particle.y += particle.vy;

        context.beginPath();
        context.globalAlpha = particle.alpha;
        context.fillStyle = particle.color;
        context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        context.fill();
      });

      context.globalAlpha = 1;

      if (!prefersReducedMotion) {
        animationFrame = window.requestAnimationFrame(draw);
      }
    };

    const handleResize = () => {
      window.cancelAnimationFrame(animationFrame);
      createParticles();
      draw();
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(canvas.parentElement || canvas);

    createParticles();
    draw();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [density, text, variant]);

  return (
    <Box
      className={['mirakel-particles', className].filter(Boolean).join(' ')}
      aria-hidden="true"
      sx={[
        {
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          opacity,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

export default MirakelParticles;
